// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { useState } from 'react'
import { API_BASE_URL } from '@/config/api'
import {
  PORTAL_MS_SSO_STORAGE_KEY,
  type PortalMsSsoPayload,
} from '@/lib/auth/microsoftPortalSso'
import { friendlyError } from '@/lib/utils/errorHandler'
import { MicrosoftLogo } from './MicrosoftLogo'

interface MicrosoftSignInButtonProps {
  disabled?: boolean
  onError: (message: string) => void
}

export function MicrosoftSignInButton({ disabled, onError }: MicrosoftSignInButtonProps) {
  const [loading, setLoading] = useState(false)

  async function handleClick() {
    setLoading(true)
    onError('')
    try {
      const res = await fetch(`${API_BASE_URL}/v1/auth/microsoft/start?client=portal`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'include',
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string }
        throw new Error(body.message ?? `Microsoft sign-in failed (${res.status})`)
      }
      const data = (await res.json()) as {
        flowId: string
        pollToken: string
        authorizationUrl: string
        expiresIn: number
      }
      const payload: PortalMsSsoPayload = {
        flowId: data.flowId,
        pollToken: data.pollToken,
        startedAt: Date.now(),
      }
      sessionStorage.setItem(PORTAL_MS_SSO_STORAGE_KEY, JSON.stringify(payload))
      window.location.assign(data.authorizationUrl)
    } catch (err) {
      setLoading(false)
      onError(friendlyError(err))
    }
  }

  return (
    <button
      type="button"
      disabled={disabled || loading}
      onClick={() => void handleClick()}
      className="group relative flex h-11 w-full items-center justify-center gap-2.5 rounded-md border border-line bg-raised px-4 text-sm font-semibold text-ink shadow-sm transition hover:border-ink-subtle hover:bg-overlay hover:shadow disabled:pointer-events-none disabled:opacity-50"
    >
      {loading ? (
        <svg className="h-4 w-4 shrink-0 animate-spin text-ink-muted" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
          <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      ) : (
        <MicrosoftLogo className="h-5 w-5 shrink-0" />
      )}
      <span>Continue with Microsoft</span>
      <span className="sr-only">Sign in with your Microsoft work or school account</span>
    </button>
  )
}
